import React, { forwardRef, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { blenderCss } from '@cld/blender';
import { globalTheme } from '@cld/component-theme';
import { ItemContext } from './ItemContext';
import { ItemProps, ItemPureProps } from './types';
import { populateBlenderVariablesForState } from './mixins';
import { useItemProps } from './useItemProps';

export const populateItemBlenderVariables = (props: ItemPureProps) => {
  if (props.isSelected && props.isFocused) {
    return populateBlenderVariablesForState('selectedHover');
  }
  if (props.isSelected) {
    return css`
      ${populateBlenderVariablesForState('selected')};
      ${props.isHoverable &&
      css`
        &:hover {
          ${populateBlenderVariablesForState('selectedHover')};
        }
      `}
    `;
  }
  if (props.isFocused) {
    return populateBlenderVariablesForState('hover');
  }
  return css`
    ${populateBlenderVariablesForState('normal')};
    ${props.isHoverable &&
    css`
      &:hover {
        ${populateBlenderVariablesForState('hover')};
      }
    `}
  `;
};

/**
 * List item build primitive. It is responsible to style the item itself. The content can be anything.
 */
export const ItemPure = styled.div.attrs<ItemPureProps>({ role: 'listitems' })`
  ${populateItemBlenderVariables};
  background-color: ${blenderCss.backgroundFromCssVariables};
  cursor: ${(props) => (props.isHoverable ? 'pointer' : 'default')};

  ${(props) =>
    props.isStriped &&
    css`
      &:not(:first-child) {
        border-top: 1px solid ${globalTheme.palette.background};
      }
    `};

  ${(props) =>
    props.borderRadius !== 'sm' &&
    css`
      &:first-of-type {
        border-top-left-radius: ${globalTheme.radius.unstable_new[props.borderRadius]};
        border-top-right-radius: ${globalTheme.radius.unstable_new[props.borderRadius]};
      }
      &:last-of-type {
        border-bottom-left-radius: ${globalTheme.radius.unstable_new[props.borderRadius]};
        border-bottom-right-radius: ${globalTheme.radius.unstable_new[props.borderRadius]};
      }
    `};
`;

/**
 * Main **build primitive** for item - element of `list`, `menu`, etc.
 * It is designed to coherently display `selected`/`hover`/`focus` state across similar `list`-like components.
 * `Item` accepts different layouts as a child component.
 * Layout `children` are responsible for positioning and styling content - including text, icons.
 * It means that typography for each text inside item is defined by layout component. It comunicates with `Item` component
 * via special context to know when to select component, etc.
 */
export const Item = forwardRef<HTMLDivElement, PropsWithChildren<ItemProps>>((props, ref) => {
  const { children, ...rest } = props;
  const itemProps = useItemProps(rest);

  return (
    <ItemPure data-test="item" {...itemProps.props} {...itemProps.rest} ref={ref}>
      <ItemContext.Provider value={{ ...itemProps.props, variant: getVariantByState(props.isSelected || false, props.variant || 'primary') }}>
        {children}
      </ItemContext.Provider>
    </ItemPure>
  );
});

const getVariantByState = (isSelected: boolean, variant: ItemPureProps['variant']) => (!isSelected && variant === 'primary' ? 'secondary' : variant);
